import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@adm-web-env/environment';
import { CityDeliveryMap, GisType, HxProductService, YaFeature, YaPolygon } from 'hx-services';
import { NzModalService } from 'ng-zorro-antd/modal';
import { PolygonPriceEditModal } from '@adm-web-app/modules/delivery-zones/polygon-price-edit/polygon-price-edit.modal';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { formatISO } from 'date-fns';
import { NgFor, NgIf } from '@angular/common';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { FormsModule } from '@angular/forms';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { GoogleMap, MapPolygon } from '@angular/google-maps';

export interface DeliveryZonePolygon {
  options: google.maps.PolygonOptions;
  vertices: google.maps.LatLngLiteral[];
  id: string;
  storeId?: number;
  priceId?: number;
  otherStores: { storeId: number; priceId: number; }[];
}

@Component({
    selector: 'hc-city-map-property',
    templateUrl: './city-map-property.component.html',
    styleUrls: ['./city-map-property.component.less'],
  imports: [NgIf, TranslocoDirective, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, NzInputDirective, FormsModule, NzSelectComponent, NzOptionComponent, NgFor, NzInputNumberComponent, NzSwitchComponent, GoogleMap, MapPolygon, TranslocoPipe]
})
export class CityMapPropertyComponent implements OnInit{
  readonly defaultCityCenter = {lat: 51.147174, lng: 71.434167};
  @Input() brandId!: number;
  @Input() cityId!: number;
  @Input() date!: Date;

  @Input()
  set data(propVal: CityDeliveryMap | undefined) {
    if (propVal) {
      try {
        if (!propVal.center) {
          propVal.center = this.defaultCityCenter as any;
        }
        if (propVal.defaultMapType) {
          this.addGisTypeOptions = this.gisTypeOptions.filter(r => r.value !== propVal.defaultMapType);
        }
        this.prop = propVal;
        this.geoJSON = JSON.stringify(this.prop.deliveryMap ?? {});
        if (this.prop.deliveryMap) {
          const features = (this.prop.deliveryMap.features ?? []) as YaFeature[];
          this.initDeliveryPolygons(features);
        }
      } catch (e) {
        // console.error(e)
      }
    }
  }

  @Output() dataChange = new EventEmitter<CityDeliveryMap | undefined>();

  googleMapsApiLoaded = false;
  prop: Partial<CityDeliveryMap> = {center: this.defaultCityCenter as any};
  geoJSON?: string;
  deliveryPolygons: DeliveryZonePolygon[] = [];
  isLoading = {
    saveDeliveryZone: false,
  };
  gisTypeOptions = Object.keys(GisType).map(key => {
    return {key: `enum.GisType.${key}`, value: key};
  });
  addGisTypeOptions = Object.keys(GisType).map(key => {
    return {key: `enum.GisType.${key}`, value: key};
  });

  constructor(
    private http: HttpClient,
    private productService: HxProductService,
    private modal: NzModalService,
    private tr: TranslocoService,
  ) {
  }

  onGeoJSONChange() {
    if (this.geoJSON) {
      try {
        this.prop.deliveryMap = JSON.parse(this.geoJSON);
        this.notify();
        if (this.prop && this.prop.deliveryMap && (this.prop.deliveryMap.features ?? []).length > 0) {
          this.initDeliveryPolygons((this.prop.deliveryMap.features ?? []) as YaFeature[]);
        }
      } catch (e) {
        console.warn(e);
      }
    }
  }

  onPolygonClicked(zone: DeliveryZonePolygon) {
    const modalRef = this.modal.create({
      nzTitle: this.tr.translate('deliveryZones.modal.editDeliveryZone.title'),
      nzContent: PolygonPriceEditModal,
      nzWidth: '80vw',
      nzFooter: null
    });
    if (modalRef.componentInstance) {
      modalRef.componentInstance.zone = JSON.parse(JSON.stringify(zone));
      modalRef.componentInstance.brandId = this.brandId;
      modalRef.componentInstance.cityId = this.cityId;
      modalRef.componentInstance.date = formatISO(this.date, {representation: 'date'});
    }
    modalRef.afterClose.subscribe({
      next: (updatedZone: DeliveryZonePolygon | undefined | null) => {
        if (updatedZone) {
          const zoneId = updatedZone?.id ? Number(updatedZone?.id) : undefined;
          if (this.prop.deliveryMap) {
            const feature = ((this.prop.deliveryMap.features ?? []) as YaFeature[]).find(ft => ft.geometry?.type === 'Polygon' && ft.id === zoneId);
            if (feature && updatedZone.priceId && updatedZone.storeId) {
              feature.properties['price-id'] = updatedZone.priceId;
              feature.properties['store-id'] = updatedZone.storeId;
              feature.properties['other-stores'] = updatedZone.otherStores.map(os => ({
                'store-id': os.storeId,
                'price-id': os.priceId,
              }));
              this.geoJSON = JSON.stringify(this.prop.deliveryMap);
              this.initDeliveryPolygons((this.prop.deliveryMap.features ?? []) as YaFeature[]);
            }
            this.notify();
          }
        }
      },
      error: err => {
        console.log('error modal', err);
      }
    });
  }

  notify() {
    this.dataChange.emit(this.prop as CityDeliveryMap);
  }

  changeDefaultType() {
    if (!this.prop.defaultMapType) {
      this.prop.mapTypes = [];
      this.prop.dgisRegionId = undefined;
      this.prop.bbox = undefined;
    } else {
      this.addGisTypeOptions = this.gisTypeOptions.filter(r => r.value !== this.prop.defaultMapType);
    }
    this.dataChange.emit(this.prop as CityDeliveryMap);
  }

  changeAdditionalType() {
    if (this.prop.mapTypes && (!this.prop.mapTypes.includes('yandex') && this.prop.defaultMapType !== 'yandex')) {
      this.prop.bbox = undefined;
    }
    if (this.prop.mapTypes && (!this.prop.mapTypes.includes('dgis') && this.prop.defaultMapType !== 'dgis')) {
      this.prop.dgisRegionId = undefined;
    }
  }

  private initDeliveryPolygons(features: YaFeature[]) {
    this.deliveryPolygons = features
      .filter(feature => feature.geometry?.type === 'Polygon')
      .map(feature => {
        const zone: DeliveryZonePolygon = {
          id: feature.id + '',
          vertices: [],
          options: {
            fillColor: feature.properties['fill'],
            fillOpacity: feature.properties['fill-opacity'],
            strokeColor: feature.properties['stroke'],
            strokeOpacity: feature.properties['stroke-opacity'],
            strokeWeight: feature.properties['stroke-width'] ? Number(feature.properties['stroke-width']) : undefined,
          },
          priceId: feature.properties['price-id'],
          storeId: feature.properties['store-id'],
          otherStores: (feature.properties['other-stores'] ?? []).map(os => ({
            storeId: os['store-id'],
            priceId: os['price-id']
          })),
        };
        const polygon = feature.geometry as YaPolygon;
        if (polygon.coordinates.length > 0) {
          zone.vertices = polygon.coordinates[0].map((point: number[]) => ({
            lat: point[1],
            lng: point[0]
          }));
        }
        return zone;
      });
  }

  ngOnInit(): void {
    this.http.jsonp(`https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}`, 'callback')
      .pipe(
        map(() => true),
        catchError(err => {
          console.error('err', err);
          return of(false);
        }),
      )
      .subscribe(result => {
        console.info('googleMapsApiLoaded: ', result);
        this.googleMapsApiLoaded = result;
      });
  }

}
